import { useState, useEffect, useRef } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { 
  ShoppingCart, User, LogOut, Shield, Home, Mail, Trash2,
  ChevronDown, ChevronUp, RefreshCw, Send, Check, MessageSquare, Inbox
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useCart } from '../context/CartContext';
import { errMsg } from '../lib/utils';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LOGO_URL = "https://customer-assets.emergentagent.com/job_91be65d6-9fee-4eda-abde-3fd83cab62ae/artifacts/kxr3j7eu_IMG_7793.png";

function MessageCard({ message, onDelete, onReplySent, autoExpand }) {
  const [expanded, setExpanded] = useState(!!autoExpand);
  const [deleting, setDeleting] = useState(false);
  const [replying, setReplying] = useState(false);
  const [replyBody, setReplyBody] = useState('');
  const [sending, setSending] = useState(false);
  // Full conversation history with this customer (their other contact messages
  // + every reply we've sent). Fetched on first expand.
  const [thread, setThread] = useState(null);   // array | null
  const [threadLoading, setThreadLoading] = useState(false);
  const cardRef = useRef(null);

  const messageId = message.id || message.sent_at;
  const replies = Array.isArray(message.replies) ? message.replies : [];

  useEffect(() => {
    if (autoExpand && cardRef.current) {
      // Scroll into view after layout settles
      const t = setTimeout(() => {
        cardRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 200);
      return () => clearTimeout(t);
    }
  }, [autoExpand]);

  const fetchThread = async () => {
    if (!message.email) return;
    setThreadLoading(true);
    try {
      const token = localStorage.getItem('blkmrkt_token');
      const res = await axios.get(`${API}/admin/contact-messages/thread`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { email: message.email },
      });
      setThread(Array.isArray(res.data?.messages) ? res.data.messages : []);
    } catch (err) {
      toast.error(errMsg(err, 'Failed to load conversation history'));
    } finally {
      setThreadLoading(false);
    }
  };

  // Fetch thread the first time the user expands the card. Re-fetch after a
  // reply is sent so the new message instantly appears in the history view.
  useEffect(() => {
    if (expanded && thread === null && !threadLoading) {
      fetchThread();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expanded]);

  const handleDelete = async () => {
    if (!window.confirm('Delete this message?')) return;
    setDeleting(true);
    try {
      await onDelete(messageId);
      toast.success('Message deleted');
    } catch (error) {
      toast.error(errMsg(error, 'Failed to delete message'));
      setDeleting(false);
    }
  };

  const handleSendReply = async () => {
    const body = replyBody.trim();
    if (!body) {
      toast.error('Reply cannot be empty');
      return;
    }
    setSending(true);
    try {
      const token = localStorage.getItem('blkmrkt_token');
      const res = await axios.post(
        `${API}/admin/contact-messages/${encodeURIComponent(messageId)}/reply`,
        { body },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Reply sent to ${message.email}`);
      setReplyBody('');
      setReplying(false);
      onReplySent?.(messageId, res.data?.reply);
      // Pull the freshly-saved reply into the thread view as well
      fetchThread();
    } catch (err) {
      toast.error(errMsg(err, 'Failed to send reply'));
    } finally {
      setSending(false);
    }
  };

  const formatDate = (dateStr) => dateStr ? new Date(dateStr).toLocaleString() : '';

  const getSubjectColor = (subject) => {
    switch (subject) {
      case 'Order Issue': return 'bg-red-500/20 text-red-400';
      case 'Payment Issue': return 'bg-orange-500/20 text-orange-400';
      case 'Shipping Question': return 'bg-blue-500/20 text-blue-400';
      case 'Product Question': return 'bg-tiffany-500/20 text-tiffany-500';
      default: return 'bg-zinc-500/20 text-zinc-400';
    }
  };

  // Build a unified chronological "conversation" timeline:
  // every inbound contact message + every outbound reply, in time order.
  const conversation = (() => {
    const source = Array.isArray(thread) ? thread : [message];
    const events = [];
    for (const m of source) {
      events.push({
        type: 'inbound',
        id: (m.id || m.sent_at) + '-msg',
        msgId: m.id || m.sent_at,
        sent_at: m.sent_at,
        from: m.name || m.email,
        subject: m.subject,
        body: m.message,
        isCurrent: (m.id || m.sent_at) === messageId,
      });
      (m.replies || []).forEach((r, i) => {
        events.push({
          type: 'outbound',
          id: (m.id || m.sent_at) + '-reply-' + i,
          msgId: m.id || m.sent_at,
          sent_at: r.sent_at,
          from: r.admin_email || 'admin',
          body: r.body,
        });
      });
    }
    events.sort((a, b) => (a.sent_at || '').localeCompare(b.sent_at || ''));
    return events;
  })();

  return (
    <div ref={cardRef} className={`border ${autoExpand ? 'border-tiffany-500/60' : 'border-zinc-800'}`} data-testid={`message-${messageId}`}>
      {/* Header - Always visible */}
      <button 
        onClick={() => setExpanded(!expanded)}
        className="w-full p-4 flex flex-wrap items-center justify-between gap-3 bg-zinc-900/30 hover:bg-zinc-900/50 transition-colors cursor-pointer text-left"
      >
        <div className="flex items-center gap-4 flex-wrap flex-1">
          <div className="min-w-[140px]">
            <p className="font-mono text-xs text-zinc-500">DATE</p>
            <p className="font-mono text-sm text-zinc-400">{formatDate(message.sent_at)}</p>
          </div>
          <div className="min-w-[120px]">
            <p className="font-mono text-xs text-zinc-500">FROM</p>
            <p className="font-mono text-sm text-white">{message.name}</p>
          </div>
          <div className="flex-1 min-w-[150px]">
            <p className="font-mono text-xs text-zinc-500">EMAIL</p>
            <p className="font-mono text-sm text-tiffany-500">{message.email}</p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          {message.replied && (
            <Badge className="bg-tiffany-500/20 text-tiffany-400 border-0 rounded-none font-mono text-[10px] tracking-widest">
              <Check className="w-3 h-3 mr-1" /> REPLIED
            </Badge>
          )}
          <Badge className={`${getSubjectColor(message.subject)} border-0 rounded-none`}>
            {message.subject}
          </Badge>
          {expanded ? (
            <ChevronUp className="w-5 h-5 text-zinc-400" />
          ) : (
            <ChevronDown className="w-5 h-5 text-zinc-400" />
          )}
        </div>
      </button>

      {/* Preview line */}
      {!expanded && (
        <div className="px-4 py-2 border-t border-zinc-800/50 bg-zinc-900/10">
          <p className="font-mono text-xs text-zinc-500 truncate">
            {message.message.substring(0, 100)}{message.message.length > 100 ? '...' : ''}
          </p>
        </div>
      )}

      {/* Expanded Content */}
      {expanded && (
        <div className="border-t border-zinc-800">
          {/* Conversation timeline */}
          <div className="p-4 space-y-3" data-testid={`conversation-${messageId}`}>
            <div className="flex items-center justify-between">
              <p className="font-mono text-[10px] text-zinc-500 tracking-widest flex items-center gap-2">
                <MessageSquare className="w-3 h-3 text-tiffany-500" />
                CONVERSATION WITH <span className="text-tiffany-500">{message.email}</span>
                {Array.isArray(thread) && thread.length > 1 && (
                  <span className="text-zinc-600">· {thread.length} TICKETS</span>
                )}
              </p>
              <button
                onClick={fetchThread}
                disabled={threadLoading}
                className="font-mono text-[10px] text-zinc-500 hover:text-tiffany-500 disabled:opacity-40 flex items-center gap-1"
                data-testid={`refresh-thread-${messageId}`}
              >
                <RefreshCw className={`w-3 h-3 ${threadLoading ? 'animate-spin' : ''}`} />
                REFRESH
              </button>
            </div>

            {threadLoading && conversation.length === 0 ? (
              <p className="font-mono text-xs text-zinc-600">LOADING THREAD…</p>
            ) : conversation.length === 0 ? (
              <p className="font-mono text-xs text-zinc-600">NO MESSAGES</p>
            ) : (
              conversation.map((evt) => (
                <div
                  key={evt.id}
                  className={`flex ${evt.type === 'outbound' ? 'justify-end' : 'justify-start'}`}
                >
                  <div
                    className={`max-w-[85%] border p-3 font-mono text-sm whitespace-pre-wrap relative ${
                      evt.type === 'outbound'
                        ? 'bg-tiffany-500/10 border-tiffany-500/40 text-tiffany-100'
                        : evt.isCurrent
                          ? 'bg-zinc-900 border-zinc-600 text-white'
                          : 'bg-zinc-900/60 border-zinc-700 text-zinc-300'
                    }`}
                  >
                    <p className={`font-mono text-[10px] tracking-widest mb-2 ${
                      evt.type === 'outbound' ? 'text-tiffany-500/80' : 'text-zinc-500'
                    }`}>
                      {evt.type === 'outbound' ? 'YOU' : (evt.from || 'CUSTOMER').toUpperCase()}
                      {evt.subject && evt.type === 'inbound' && (
                        <span className="text-zinc-600"> · {evt.subject.toUpperCase()}</span>
                      )}
                      <span className="text-zinc-600 ml-2">· {formatDate(evt.sent_at)}</span>
                      {evt.isCurrent && (
                        <span className="ml-2 px-1.5 py-0.5 bg-tiffany-500 text-black text-[9px] tracking-widest">CURRENT</span>
                      )}
                    </p>
                    {evt.body}
                  </div>
                </div>
              ))
            )}
          </div>

          {/* Reply composer */}
          {replying ? (
            <div className="border-t border-zinc-800 p-4 space-y-2" data-testid={`reply-composer-${messageId}`}>
              <p className="font-mono text-xs text-zinc-500">
                REPLY TO <span className="text-tiffany-500">{message.email}</span>
                <span className="text-zinc-600"> · FROM support@shopblkmrkt.com</span>
              </p>
              <textarea
                value={replyBody}
                onChange={(e) => setReplyBody(e.target.value)}
                placeholder="Type your reply... will be sent as a stylized BLK MRKT email."
                rows={6}
                disabled={sending}
                className="w-full bg-zinc-900 border border-zinc-700 focus:border-tiffany-500 outline-none p-3 font-mono text-sm text-white resize-y"
                data-testid={`reply-textarea-${messageId}`}
                maxLength={5000}
              />
              <div className="flex items-center justify-between text-xs font-mono text-zinc-600">
                <span>{replyBody.length} / 5000</span>
                <span className="text-zinc-500">Customer sees BLK MRKT branded email · replies route back to support@</span>
              </div>
              <div className="flex justify-end gap-2 pt-1">
                <Button
                  onClick={() => { setReplying(false); setReplyBody(''); }}
                  disabled={sending}
                  variant="outline"
                  className="rounded-none border-zinc-700 text-zinc-400"
                  data-testid={`reply-cancel-${messageId}`}
                >CANCEL</Button>
                <Button
                  onClick={handleSendReply}
                  disabled={sending || !replyBody.trim()}
                  className="rounded-none bg-tiffany-500 text-black hover:bg-tiffany-400 disabled:opacity-50"
                  data-testid={`reply-send-${messageId}`}
                >
                  <Send className="w-4 h-4 mr-2" />
                  {sending ? 'SENDING…' : 'SEND REPLY'}
                </Button>
              </div>
            </div>
          ) : (
            <div className="p-4 border-t border-zinc-800 flex justify-between items-center gap-2">
              <Button
                onClick={handleDelete}
                disabled={deleting}
                variant="outline"
                className="rounded-none border-red-800 text-red-500 hover:bg-red-500/20"
                data-testid={`delete-${messageId}`}
              >
                <Trash2 className="w-4 h-4 mr-2" />
                {deleting ? 'DELETING...' : 'DELETE'}
              </Button>
              
              <Button
                onClick={() => setReplying(true)}
                className="rounded-none bg-tiffany-500 text-black font-sans tracking-widest text-sm hover:bg-tiffany-400"
                data-testid={`reply-open-${messageId}`}
              >
                <Send className="w-4 h-4 mr-2" />
                {replies.length > 0 ? 'REPLY AGAIN' : 'REPLY'}
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function AdminContactMessages() {
  const { user, logout } = useAuth();
  const { itemCount } = useCart();
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchParams, setSearchParams] = useSearchParams();
  // Id of the message that should auto-expand on mount (set by the admin
  // notification email deep-link `?focus=<id>`).
  const focusId = searchParams.get('focus') || null;
  const [hasConsumedFocus, setHasConsumedFocus] = useState(false);

  const getAuthHeader = () => {
    const token = localStorage.getItem('blkmrkt_token');
    return { Authorization: `Bearer ${token}` };
  };

  const fetchMessages = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/admin/contact-messages`, { headers: getAuthHeader() });
      setMessages(response.data.messages || []);
    } catch (error) {
      console.error('Failed to fetch contact messages:', error);
      toast.error('Failed to load messages');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMessages();
  }, []);

  const handleDelete = async (messageId) => {
    await axios.delete(`${API}/admin/contact-messages/${encodeURIComponent(messageId)}`, { headers: getAuthHeader() });
    setMessages(messages.filter(m => (m.id || m.sent_at) !== messageId));
  };

  const handleReplySent = (messageId, reply) => {
    // Optimistically merge the new reply into the local thread so the admin
    // sees it immediately without another roundtrip.
    setMessages(prev => prev.map(m => {
      if ((m.id || m.sent_at) !== messageId) return m;
      const existing = Array.isArray(m.replies) ? m.replies : [];
      return {
        ...m,
        replies: reply ? [...existing, reply] : existing,
        replied: true,
        last_replied_at: reply?.sent_at,
      };
    }));
  };

  return (
    <div className="min-h-screen bg-[#050505]">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 border-b border-zinc-800 bg-[#050505]/90 backdrop-blur-sm">
        <div className="max-w-7xl mx-auto px-4 md:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/catalog" className="flex items-center gap-3">
              <img src={LOGO_URL} alt="BLK MRKT" className="h-8 w-auto" />
            </Link>
            <Badge className="bg-tiffany-500 text-black rounded-none border-0 font-mono">
              ADMIN
            </Badge>
          </div>
          
          <nav className="flex items-center gap-4">
            <Link to="/catalog" className="p-2 text-zinc-400 hover:text-tiffany-500 transition-colors">
              <Home className="w-5 h-5" />
            </Link>
            <Link to="/cart" className="relative p-2 text-zinc-400 hover:text-tiffany-500 transition-colors">
              <ShoppingCart className="w-5 h-5" />
              {itemCount > 0 && (
                <span className="absolute -top-1 -right-1 bg-tiffany-500 text-black text-xs font-bold w-5 h-5 flex items-center justify-center">
                  {itemCount}
                </span>
              )}
            </Link>
            <Link to="/dashboard" className="p-2 text-zinc-400 hover:text-tiffany-500 transition-colors">
              <User className="w-5 h-5" />
            </Link>
            <Link to="/admin" className="p-2 text-zinc-400 hover:text-tiffany-500 transition-colors">
              <Shield className="w-5 h-5" />
            </Link>
            <button onClick={logout} className="p-2 text-zinc-400 hover:text-red-500 transition-colors">
              <LogOut className="w-5 h-5" />
            </button>
          </nav>
        </div>
      </header>

      {/* Main Content */}
      <main className="pt-24 pb-12 px-4 md:px-8 max-w-5xl mx-auto">
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-3">
            <Mail className="w-6 h-6 text-tiffany-500" />
            <h1 className="font-sans text-3xl text-white">CONTACT MESSAGES</h1>
            <span className="font-mono text-zinc-500">({messages.length})</span>
          </div>
          <Button
            onClick={fetchMessages}
            variant="outline"
            className="rounded-none border-zinc-700 text-zinc-400 hover:text-tiffany-500 hover:border-tiffany-500"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            REFRESH
          </Button>
        </div>

        {loading ? (
          <div className="text-center py-12">
            <p className="font-mono text-zinc-500">Loading messages...</p>
          </div>
        ) : messages.length === 0 ? (
          <div className="border border-zinc-800 p-12 text-center">
            <Mail className="w-12 h-12 text-zinc-700 mx-auto mb-4" />
            <p className="font-mono text-zinc-500">NO MESSAGES YET</p>
            <p className="font-mono text-xs text-zinc-600 mt-2">
              Contact messages from customers will appear here
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {focusId && (
              <div className="bg-tiffany-500/10 border border-tiffany-500/40 p-3 font-mono text-xs text-tiffany-300 flex items-center justify-between gap-3" data-testid="focus-banner">
                <span className="flex items-center gap-2">
                  <Inbox className="w-3.5 h-3.5" />
                  OPENED FROM EMAIL NOTIFICATION
                </span>
                <button
                  onClick={() => { searchParams.delete('focus'); setSearchParams(searchParams); setHasConsumedFocus(true); }}
                  className="text-tiffany-300 hover:text-tiffany-200 underline"
                >CLEAR</button>
              </div>
            )}
            {/* Auto-promote the focused message to the top of the list so the
                admin doesn't have to scroll past 20 older messages to see it. */}
            {(focusId && !hasConsumedFocus
              ? [...messages].sort((a, b) => {
                  const aId = a.id || a.sent_at;
                  const bId = b.id || b.sent_at;
                  if (aId === focusId) return -1;
                  if (bId === focusId) return 1;
                  return 0;
                })
              : messages
            ).map((message, index) => {
              const mid = message.id || message.sent_at;
              return (
                <MessageCard 
                  key={mid || index} 
                  message={message} 
                  onDelete={handleDelete}
                  onReplySent={handleReplySent}
                  autoExpand={!hasConsumedFocus && focusId && mid === focusId}
                />
              );
            })}
          </div>
        )}

        {/* Back to Admin Link */}
        <div className="mt-8 text-center">
          <Link 
            to="/admin" 
            className="font-mono text-sm text-zinc-500 hover:text-tiffany-500 transition-colors"
          >
            ← BACK TO ADMIN DASHBOARD
          </Link>
        </div>
      </main>
    </div>
  );
}
